/* Activate legacy v2 — seven-step wizard (production fallback when v3 preview off). */

import { useState, useMemo, useEffect, useRef } from "react";
import { AnimatePresence } from "framer-motion";
import { html, M, F, Ico, KV, Card, Field, Input, Alert,
         SOFT, FADE, go, stay } from "./ui.js";
import { Board, BoardKey, PortList, PortDetail } from "./board.js";
import { path, useApi, useAction } from "./api.js";
import { envelopeSummary } from "./action-envelope.js";
import { useSession } from "./session.js";
import * as E from "./edge-data.js";
import { capabilityCatalogOf, boardLoopViewOf } from "./edgeos-live.js";
import { safeBoardPath } from "./board-api.js";
import { setDirtySeat } from "./quality-runtime.js";
import {
  KEYS,
  STEPS,
  CLAIMABLE,
  activateMaxStepOf,
  activateSeatOf,
  safeActivatePath,
  softActivateErrorOf,
  fmtMac,
  hwIdFromMac,
  hintsOf,
  normalizeQrPayload,
} from "./activate-shared.js";

const STEP_COPY = [
  "Find the physical unit",
  "Prove it is in front of you",
  "Bring it into custody",
  "Confirm connected hardware",
  "Choose what it can do",
  "Record where it belongs",
  "Hand off to Machine",
];
const ACTIVATE_STAGES = [
  { id:"discover", label:"Discover", say:"Find and verify", steps:[0,1] },
  { id:"secure", label:"Secure", say:"Take custody", steps:[2] },
  { id:"configure", label:"Configure", say:"Interfaces and abilities", steps:[3,4] },
  { id:"place", label:"Place", say:"Location and handoff", steps:[5,6] },
];

export default function ActivateLegacy({ sub, toast }){
  const routeAt = Math.max(0, KEYS.indexOf(KEYS.includes(sub) ? sub : "identify"));
  const [at, setAt] = useState(routeAt);
  useEffect(() => setAt(routeAt), [routeAt]);
  const { me } = useSession();
  const role = String((me && me.role) || "").toLowerCase();
  const posture = useApi(safeActivatePath("get__activate_status"));
  const post = posture.data || {};
  /* Fail-closed while load — never invent open Activate seats from me.role. */
  const seat = posture.loading
    ? activateSeatOf({ enabled: false, principal_role: role, limits: { pending_paint_max: 48 } })
    : activateSeatOf(post);

  const [serial,setSerial] = useState("");
  const [deviceId,setDeviceId] = useState("");
  const [serviceId,setServiceId] = useState("");
  const [zone,setZone] = useState("all");
  const [identify,setIdentify] = useState(null);
  const [claimOut,setClaimOut] = useState(null);
  /* Geometry PORTS for orientation only — never seed claimed caps or pin “verified”. */
  const [ports] = useState(E.PORTS);
  const [caps,setCaps] = useState([]);

  /* QUALITY-1: draft MAC / id on Activate marks dirty seat for leave/refresh guard. */
  useEffect(() => {
    const dirty = !!(String(serial || "").trim() || String(deviceId || "").trim());
    setDirtySeat(dirty);
    return () => { try { setDirtySeat(false); } catch (_) { /* ignore */ } };
  }, [serial, deviceId]);

  const reReadPosture = () => {
    if (posture.loading) {
      toast && toast("Confirming Activate seat…", "warn");
      return;
    }
    if (!seat.reReadAllowed && !posture.error) {
      toast && toast("Re-read is closed for this seat", "warn");
      return;
    }
    posture.reload && posture.reload();
    toast && toast("Activate seat re-read", "go");
  };

  const maxStep = activateMaxStepOf({ identify, claimOut, deviceId });
  const PANE = [Identify, Verify, Claim, Interfaces, Capabilities, Position, Done][at];
  const goStep = (i) => {
    if (i < 0 || i >= KEYS.length) return;
    if (i > maxStep) {
      toast && toast("Finish the earlier step first — custody cannot be skipped", "warn");
      return;
    }
    setAt(i);
    stay("/activate/" + KEYS[i]);
  };
  const step = { serial, setSerial, deviceId, setDeviceId,
                 serviceId, setServiceId, zone, setZone,
                 identify, setIdentify, claimOut, setClaimOut,
                 ports, caps, setCaps, toast, seat, goStep };

  return html`
    <div className="sheet act-desk act-desk--liquid desk-craft activate-v2"
      data-cmc-page="page95-activate" data-cmc-activate="page17"
      data-cmc-residual="page214-activate" data-cmc-shell="activate-ux"
      data-cmc-activate-ux="activate-v2">
      <div className="wrap activate-v2__wrap">
        <header className="activate-v2__hero">
          <div>
            <p className="eyebrow">Independent registration workspace</p>
            <h1 className="ph act-desk__title">Bring a machine under control</h1>
            <p className="psub">Seven guarded checks, grouped into four clear stages. Nothing joins your estate until identity and possession are proven.</p>
            <div className="activate-v2__seatline" aria-label="Activate seats">
              <span data-on=${seat.queueAllowed?"1":"0"}><i/>Discovery ${seat.queueAllowed?"open":"closed"}</span>
              <span data-on=${seat.custodyAllowed?"1":"0"}><i/>Custody ${seat.custodyAllowed?"open":"closed"}</span>
              <span data-on=${seat.wireAllowed?"1":"0"}><i/>Configuration ${seat.wireAllowed?"open":"closed"}</span>
              <em>${seat.role || role || "account"}</em>
            </div>
          </div>
          <div className="activate-v2__passage" aria-hidden="true" style=${{"--act-progress":at/(STEPS.length-1)}}>
            <span className="activate-v2__passage-track"/>
            <${M.div} className="activate-v2__unit" animate=${{left:(at/(STEPS.length-1)*100)+"%"}}
              transition=${SOFT}><i/><i/><b>${at+1}</b><//>
            ${ACTIVATE_STAGES.map((g,i)=>html`<i key=${g.id} style=${{left:(i/3*100)+"%"}}/>`)}
          </div>
          <div className="activate-v2__hero-tools">
            <button type="button" className="btn btn--ghost btn--sm"
              disabled=${posture.loading || (!seat.reReadAllowed && !posture.error)} onClick=${reReadPosture}>
              <${Ico} d="refresh" w=${13}/> Re-check access</button>
            <button type="button" className="btn btn--ghost btn--sm" onClick=${()=>go("/machine")}>
              Return to Machine</button>
          </div>
        </header>

        ${posture.loading ? html`<${Alert} tone="info" title="Checking registration access"
          say="Confirming which discovery, custody and configuration actions are available…"/>`
        : posture.error ? html`<${Alert} tone="warn" title="Could not confirm registration access"
          say=${softActivateErrorOf(posture.error)}/>`
        : (!seat.queueAllowed || !seat.custodyAllowed) ? html`<${Alert} tone="warn"
          title="Part of this workflow is closed"
          say=${!seat.queueAllowed
            ? (softActivateErrorOf(seat.detail) || "Discovery needs an Activate operator seat.")
            : "Claim and post-claim setup need a custody seat."}/>` : null}

        <div className="activate-v2__workspace">
          <aside className="activate-v2__journey">
            <div className="activate-v2__journey-head"><span>Your progress</span><b>${at+1} / ${STEPS.length}</b></div>
            <div className="act__rail">
              ${ACTIVATE_STAGES.map((group,gi)=>html`<section key=${group.id}
                data-on=${group.steps.includes(at)?"1":"0"}
                data-done=${Math.max(...group.steps)<at?"1":"0"}>
                <header><i>${gi+1}</i><span><b>${group.label}</b><small>${group.say}</small></span></header>
                <div>
                  ${group.steps.map((i)=>html`<button key=${KEYS[i]} className="steps__s"
                    data-on=${i===at?"1":"0"} data-done=${i<at?"1":"0"} data-locked=${i>maxStep?"1":"0"}
                    disabled=${i>maxStep}
                    title=${i>maxStep?"Finish earlier steps before opening "+STEPS[i]:STEPS[i]}
                    onClick=${()=>goStep(i)}>
                    <u>${i<at?html`<${Ico} d="check" w=${9}/>`:i+1}</u>
                    <span><b>${STEPS[i]}</b><small>${STEP_COPY[i]}</small></span>
                  </button>`)}
                </div>
              </section>`)}
            </div>
            <div className="activate-v2__board-note">
              <small>Expected board</small><b>${E.BOARD.sku}</b>
              <span>${E.BOARD.mcu} · ${E.BOARD.radio}</span>
            </div>
          </aside>

          <main className="activate-v2__stage">
            <div className="activate-v2__stage-head">
              <span><small>Step ${at+1}</small><b>${STEPS[at]}</b></span>
              <p>${STEP_COPY[at]}</p>
            </div>
            <${AnimatePresence} mode="wait">
              <${M.div} key=${KEYS[at]} initial=${{ opacity:0, x:12 }} animate=${{ opacity:1, x:0 }}
                  exit=${{ opacity:0, x:-8 }} transition=${FADE}>
                <${PANE} ...${step}/>
              <//>
            <//>
          </main>
        </div>

        <div className="act__nav activate-v2__nav">
          ${at>0 && html`<button type="button" className="btn btn--ghost" onClick=${()=>goStep(at-1)}>
            <${Ico} d="arrowL" w=${14}/> ${STEPS[at-1]}</button>`}
          <span style=${{ flex:1 }}/>
          ${at<STEPS.length-1 && html`<button type="button" className="btn btn--ghost"
            disabled=${(at+1)>maxStep}
            title=${(at+1)>maxStep
              ? "Finish this step before continuing — custody cannot be skipped"
              : (!seat.custodyAllowed && at >= 1 ? "Custody is closed — nothing is claimed until open" : "Next step")}
            onClick=${()=>goStep(at+1)}>
            ${STEPS[at+1]} · next <${Ico} d="arrowR" w=${14}/></button>`}
          ${at===STEPS.length-1 && html`<button type="button" className="btn btn--go"
            disabled=${!(deviceId || (claimOut && claimOut.device_id))}
            title=${!(deviceId || (claimOut && claimOut.device_id)) ? "Claim a device first" : "Open the new machine"}
            onClick=${()=>{
              const id = String(deviceId || (claimOut && claimOut.device_id) || "").trim();
              if (!id) { toast && toast("Claim a device first", "warn"); return; }
              go("/machine/"+encodeURIComponent(id));
            }}>
            Open this machine <${Ico} d="arrowR" w=${14}/></button>`}
        </div>
      </div>
    </div>`;
}

/* ═══════════ 1 · identify ══════════════════════════════════════════════ */
function Identify({ serial, setSerial, identify, setIdentify,
                    setDeviceId, setServiceId, toast, seat, goStep }){
  const { run, busy, error, envelope } = useAction();
  const queueOn = !!(seat && seat.queueAllowed);
  const pendingOn = !!(seat && seat.pendingAllowed === true);
  const pending = useApi(pendingOn ? safeActivatePath("get__provision_pending") : null);
  const allRows = (pending.data && (pending.data.items || pending.data.pending)) || [];
  const paintMax = (seat && seat.pendingPaintMax) || 48;
  const rows = Array.isArray(allRows) ? allRows.slice(0, paintMax) : [];
  const live = identify;
  const status = live && live.status;
  const claimable = !!(live && CLAIMABLE.has(status) && live.mac_nocolon);
  const [camOn, setCamOn] = useState(false);
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const detectorRef = useRef(null);
  const scanLoopRef = useRef(0);

  const stopCamera = () => {
    scanLoopRef.current += 1;
    const stream = streamRef.current;
    streamRef.current = null;
    if (stream) {
      try { stream.getTracks().forEach(t => t.stop()); } catch (_) {}
    }
    const v = videoRef.current;
    if (v) {
      try { v.srcObject = null; } catch (_) {}
    }
    setCamOn(false);
  };

  useEffect(() => () => { stopCamera(); }, []);

  const applyQr = (raw, how) => {
    const t = normalizeQrPayload(raw);
    if (!t) {
      toast("No QR text — copy the payload or scan again", "warn");
      return false;
    }
    setSerial(t);
    toast("QR loaded · " + how, "go");
    return true;
  };

  const pasteQrLive = async () => {
    try {
      if (!navigator.clipboard || typeof navigator.clipboard.readText !== "function") {
        toast("Clipboard API unavailable — paste into the field (Ctrl+V)", "warn");
        return;
      }
      const t = await navigator.clipboard.readText();
      applyQr(t, "clipboard");
    } catch (_) {
      toast("Clipboard denied — paste into the field (Ctrl+V)", "warn");
    }
  };

  const startCameraQr = async () => {
    if (typeof window.BarcodeDetector !== "function") {
      toast("BarcodeDetector unavailable in this browser — use Paste QR", "warn");
      return;
    }
    if (!navigator.mediaDevices || typeof navigator.mediaDevices.getUserMedia !== "function") {
      toast("Camera API unavailable — use Paste QR", "warn");
      return;
    }
    stopCamera();
    try {
      detectorRef.current = new window.BarcodeDetector({ formats: ["qr_code"] });
    } catch (_) {
      try { detectorRef.current = new window.BarcodeDetector(); }
      catch {
        toast("BarcodeDetector refused — use Paste QR", "warn");
        return;
      }
    }
    let stream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({
        audio: false,
        video: { facingMode: { ideal: "environment" } },
      });
    } catch (_) {
      toast("Camera permission denied — use Paste QR", "warn");
      return;
    }
    streamRef.current = stream;
    setCamOn(true);
    const token = ++scanLoopRef.current;
    // Wait one frame so the video node mounts.
    requestAnimationFrame(() => {
      const v = videoRef.current;
      if (!v || token !== scanLoopRef.current) {
        try { stream.getTracks().forEach(t => t.stop()); } catch (_) {}
        return;
      }
      v.srcObject = stream;
      v.play().catch(() => {});
      const tick = async () => {
        if (token !== scanLoopRef.current) return;
        const video = videoRef.current;
        const det = detectorRef.current;
        if (video && det && video.readyState >= 2) {
          try {
            const codes = await det.detect(video);
            if (codes && codes.length) {
              const raw = codes[0].rawValue || codes[0].rawData || "";
              if (applyQr(raw, "camera")) {
                stopCamera();
                return;
              }
            }
          } catch (_) { /* keep scanning */ }
        }
        if (token === scanLoopRef.current) {
          setTimeout(tick, 280);
        }
      };
      setTimeout(tick, 320);
    });
  };

  const runIdentify = async () => {
    if (!queueOn || !(seat && seat.identifyAllowed)) {
      toast("Identify is closed for this seat", "warn");
      return;
    }
    const s = String(serial || "").trim().toUpperCase();
    if (!s) { toast("Enter MAC, CROC-hw id, SN, or QR first", "warn"); return; }
    const hex = s.replace(/[^0-9A-F]/g, "");
    let body;
    if (hex.length === 12 && !s.startsWith("SN-") && !s.startsWith("CROC-|")) {
      body = s.startsWith("CROC-") ? { serial: s } : { mac_nocolon: hex };
    } else if (s.includes("|") || s.length > 23) {
      body = { qr_code: s };
    } else {
      body = { serial: s };
    }
    const out = await run(safeActivatePath("post__provision_identify"), { body });
    if (!out) { toast("Identify failed — see the panel", "bad"); return; }
    setIdentify(out);
    const hw = out.hw_id || out.proposed_device_id || hwIdFromMac(out.mac_nocolon);
    const proposed = String(hw || out.serial || out.device_id || "").trim();
    if (proposed) setDeviceId(proposed);
    if (out.service_id) setServiceId(String(out.service_id).trim());
    toast(out.status + (out.message ? " · " + String(out.message).slice(0, 80) : ""), "go");
  };

  const pickPending = async (r) => {
    if (!queueOn || !(seat && seat.identifyAllowed) || !pendingOn) {
      toast("Select is closed for this seat", "warn");
      return;
    }
    const mac = String(r.mac_nocolon || "").replace(/[^0-9A-Fa-f]/g, "").toUpperCase();
    const hw = hwIdFromMac(mac);
    const proposed = hw || String(r.proposed_device_id || r.device_id || "").trim();
    setSerial(mac || proposed);
    setDeviceId(proposed);
    setServiceId("");
    /* Fail-closed: never invent status=ready. Live identify only. */
    setIdentify(null);
    if (mac.length !== 12) {
      toast("Pending row has no usable MAC · identify manually", "warn");
      return;
    }
    const out = await run(safeActivatePath("post__provision_identify"), {
      body: { mac_nocolon: mac },
    });
    if (!out) {
      toast("Identify after select refused", "bad");
      return;
    }
    setIdentify(out);
    const outHw = out.hw_id || out.proposed_device_id || hwIdFromMac(out.mac_nocolon);
    const nextId = String(outHw || out.serial || out.device_id || "").trim();
    if (nextId) setDeviceId(nextId);
    if (out.service_id) setServiceId(String(out.service_id).trim());
    toast("Selected · identify " + String(out.status || ""), "go");
    if (CLAIMABLE.has(String(out.status || ""))) {
      goStep(2);
    }
  };

  const toneOf = (st) =>
    CLAIMABLE.has(st) ? "go" : st === "already_registered" || st === "offline" ? "warn" : "bad";

  return html`
    <div className="grid g-a">
      ${envelope
        ? html`<${KV} k="Last action" v=${envelopeSummary(envelope) || "—"} tone=${envelope.ok ? "hi" : "warn"}/>`
        : null}
      <${Card} title="Pending queue (prefer)" meta="waiting boards · prefer this list">
        <p className="sub">Boards that already checked in. Pick one → claim with
          <code>${"CROC-" + "<" + "MAC>"}</code> as hardware id. No SN typing required.</p>
        ${!queueOn || !pendingOn ? html`<${Alert} tone="warn" title="Queue is closed for this seat"
          say=${softActivateErrorOf(seat && seat.detail) || "Pending list needs an activate operator seat."}/>` : null}
        <div className="btn-row act-desk__acts" style=${{ marginBottom:10 }}>
          <button type="button" className="btn btn--ghost btn--sm"
            disabled=${!pendingOn || !(seat && seat.reReadAllowed)}
            onClick=${()=>{
              if (!pendingOn) { toast("Queue is closed for this seat", "warn"); return; }
              pending.reload && pending.reload();
              toast("Pending queue re-read", "go");
            }}>
            <${Ico} d="refresh" w=${13}/> Re-read</button>
        </div>
        ${!pendingOn ? null : pending.error ? html`<${Alert} tone="warn" title="Pending list unavailable" say=${softActivateErrorOf(pending.error)}/>` :
          html`<table className="tbl">
              <thead><tr><th>MAC</th><th>QR / alias</th><th>FW</th><th>Seen</th><th>Proposed</th><th/></tr></thead>
              <tbody>
                ${!rows.length ? html`<tr><td colSpan=${6}><p className="note">${pending.loading ? "Reading…" : "No pending devices right now — use MAC identify below, or power the board."}</p></td></tr>` : null}
                ${rows.map(r=> html`
                  <tr key=${r.mac_nocolon || r.mac}>
                    <td><code>${fmtMac(r.mac_nocolon) || r.mac || "—"}</code></td>
                    <td style=${{ color:"var(--fg-3)" }}><code>${r.qr_code || "—"}</code></td>
                    <td>${r.fw || "—"}</td>
                    <td>${r.last_seen_at || "—"}</td>
                    <td><code>${hwIdFromMac(r.mac_nocolon) || r.proposed_device_id || "—"}</code></td>
                    <td style=${{ textAlign:"right" }}><button type="button" className="btn btn--sm btn--go"
                      disabled=${busy || !pendingOn || !(seat && seat.identifyAllowed)}
                      onClick=${()=>pickPending(r)}>Select</button></td>
                  </tr>`)}
              </tbody>
            </table>`}
      <//>

      <${Card} title="MAC · hw id · or SN" meta="find this board">
        <p className="sub">Type 12-hex MAC, <code>${"CROC-" + "<" + "MAC>"}</code>, optional sticker
          <code>SN-…</code>, or load a QR via clipboard / camera.
          Nothing is claimed by Identify.</p>
        <${Field} hint="E8F60AE0AFD2 · CROC-E8F60AE0AFD2 · SN-… · or CROC|…| QR">
          <${Input} icon="chip" value=${serial} onChange=${e=>setSerial(e.target.value)}
            act="Paste QR" onAct=${pasteQrLive}/>
        <//>
        <div className="btn-row act-desk__acts" style=${{ marginTop:12 }}>
          <button type="button" className="btn btn--go btn--sm"
            disabled=${busy || !queueOn || !(seat && seat.identifyAllowed)}
            onClick=${runIdentify}>
            ${busy ? "Identifying…" : "Identify"}</button>
          <button className="btn btn--ghost btn--sm" type="button"
            onClick=${camOn ? stopCamera : startCameraQr}>
            <${Ico} d="chip" w=${13}/> ${camOn ? "Stop camera" : "Camera QR"}</button>
          ${claimable && html`<button type="button" className="btn btn--ghost btn--sm"
            onClick=${()=>goStep(2)}>Continue to claim</button>`}
          ${claimable && live && live.pop_pinned && html`<button type="button" className="btn btn--ghost btn--sm"
            onClick=${()=>goStep(2)}>PoP pinned · skip verify</button>`}
        </div>
        ${camOn ? html`
          <div className="act__cam" style=${{
            marginTop:12, borderRadius:12, overflow:"hidden",
            border:"1px solid var(--line)", background:"#0b1220",
          }}>
            <video ref=${videoRef} muted playsInline
              style=${{ width:"100%", maxHeight:240, objectFit:"cover", display:"block" }}/>
            <p className="hint" style=${{ padding:"8px 10px", margin:0 }}>
              Point the camera at the board QR. Stop when done.</p>
          </div>` : null}
        ${error && html`<${Alert} tone="warn" title="Identify refused" say=${softActivateErrorOf(error)}/>`}
      <//>

      <${Card} title="What came back" meta=${live ? String(status || "ready") : "empty"} ai=${false}>
        ${live ? html`<${F}>
          <${Alert} tone=${toneOf(status)}
            icon=${CLAIMABLE.has(status)?"check":"info"}
            title=${status} say=${live.message || "Server identify result."}/>
          <${KV} k="Hardware id" v=${live.hw_id || live.proposed_device_id || "—"}/>
          <${KV} k="Service S/N" v=${live.service_id || "—"}/>
          <${KV} k="Factory serial" v=${live.serial || live.device_id || "—"}/>
          <${KV} k="MAC" v=${fmtMac(live.mac_nocolon)}/>
          <${KV} k="PoP pinned" v=${live.pop_pinned ? "yes" : "no"} tone=${live.pop_pinned ? "good" : undefined}/>
          <${KV} k="Firmware" v=${live.fw || "—"}/>
          ${live.last_seen_at && html`<${KV} k="Last check-in" v=${String(live.last_seen_at)}/>`}
          ${live.claimed_at && html`<${KV} k="Claimed at" v=${String(live.claimed_at)}/>`}
          ${hintsOf(live).length > 0 && html`
            <div className="opsteps">
              <u>Tell the person holding the board</u>
              ${hintsOf(live).map((h,i)=> html`<div key=${i} className="opstep"><b>${i+1}</b><span>${h}</span></div>`)}
            </div>`}
          ${!CLAIMABLE.has(status) && status && html`<p className="note" style=${{ color:"var(--coral)" }}>
            This status is not claimable from the wizard until the board reaches ready / ready_factory.</p>`}
        <//>` : html`<${Alert} tone="info" title="No Live identify yet"
            say="Pick a pending row or Identify with MAC / CROC-hw / SN. Empty means nothing identified yet."/>`}
      <//>
    </div>`;
}

/* ═══════════ 2 · verify — PoP-first; legacy PEM paste as fallback ═══════ */
function Verify({ identify, deviceId, toast, seat, goStep }){
  const { run, busy, error, envelope } = useAction();
  const custodyOn = !!(seat && seat.custodyAllowed);
  const challengeOn = !!(seat && seat.challengeAllowed);
  const [pem, setPem] = useState("");
  const [sig, setSig] = useState("");
  const [chal, setChal] = useState(null);
  const mac = String((identify && identify.mac_nocolon) || "").replace(/[^0-9A-Fa-f]/g, "").toUpperCase();
  const did = String(deviceId || (identify && (identify.hw_id || identify.serial || identify.device_id)) || "").trim().toUpperCase();
  const popPinned = !!(identify && identify.pop_pinned);

  const requestChallenge = async () => {
    if (!custodyOn || !challengeOn) {
      toast("Proof request is closed for this seat", "warn"); return;
    }
    if (mac.length !== 12 || !did) {
      toast("Identify a claimable device first (MAC + device id)", "warn"); return;
    }
    if (pem.trim().length < 64) {
      toast("Paste the board public key (PEM) from the factory pack", "warn"); return;
    }
    const out = await run(safeActivatePath("post__provision_challenge_request"), {
      body: { mac_nocolon: mac, device_id: did, public_key_pem: pem.trim() },
    });
    if (!out) { toast("Challenge request refused", "bad"); return; }
    setChal(out);
    toast("Challenge minted · have the device sign the nonce", "go");
  };

  const verifySig = async () => {
    if (!custodyOn || !challengeOn) {
      toast("Proof request is closed for this seat", "warn"); return;
    }
    if (!chal || !chal.challenge_id) {
      toast("Request a challenge first", "warn"); return;
    }
    if (sig.trim().length < 32) {
      toast("Paste signature_b64 from the device", "warn"); return;
    }
    const out = await run(safeActivatePath("post__provision_challenge_verify"), {
      body: { challenge_id: Number(chal.challenge_id), signature_b64: sig.trim() },
    });
    if (!out) { toast("Verify refused", "bad"); return; }
    toast("Signature accepted · audit written", "go");
    goStep(2);
  };

  return html`
    <div className="grid g-a">
      ${envelope
        ? html`<${KV} k="Last action" v=${envelopeSummary(envelope) || "—"} tone=${envelope.ok ? "hi" : "warn"}/>`
        : null}
      <${Card} title="Proof of possession" meta="Proof of possession · optional PEM fallback">
        ${!custodyOn ? html`<${Alert} tone="warn" title="Custody is closed for this seat"
          say=${softActivateErrorOf(seat && seat.detail) || "Challenge / claim need activate operator."}/>` : null}
        ${popPinned ? html`<${Alert} tone="go" title="PoP already pinned on server"
          say="This unit completed boot-sync identity pin. Legacy key paste is unnecessary — continue to claim."/>` :
          html`<${Alert} tone="info" title="Awaiting board proof or legacy key"
            say="Preferred: power the board so boot-sync pins the software key. Legacy public-key paste below is only when enrollment is off — not a stand-in for enrollment."/>`}
        <p className="sub">When site claim policy requires a pinned identity, claim needs a board that already proved possession.
          A separate challenge with a pasted key is available only when that path is armed for the site.</p>
        <${KV} k="Hardware id" v=${did || "—"}/>
        <${KV} k="MAC" v=${fmtMac(mac)}/>
        <${KV} k="PoP pinned" v=${popPinned ? "yes" : "no"} tone=${popPinned ? "good" : undefined}/>
        <div className="btn-row" style=${{ marginTop:10 }}>
          <button type="button" className="btn btn--go btn--sm" onClick=${()=>goStep(2)}>
            ${popPinned ? "Continue to claim" : "Skip legacy PEM · go to claim"}</button>
        </div>
        <${Field} label="public_key_pem (legacy fallback)" hint="Only if enrollment is off and challenge is armed">
          <textarea className="inp" rows=${5} value=${pem} onChange=${e=>setPem(e.target.value)}
            placeholder="-----BEGIN PUBLIC KEY-----"/>
        <//>
        <div className="btn-row" style=${{ marginTop:10 }}>
          <button type="button" className="btn btn--ghost btn--sm"
            disabled=${busy || !custodyOn || !challengeOn}
            onClick=${requestChallenge}>
            ${busy ? "…" : "Request challenge"}</button>
        </div>
        ${chal && html`<div className="chal" style=${{ marginTop:14 }}>
          <div className="chal__r"><u>challenge_id</u><code>${chal.challenge_id}</code></div>
          <div className="chal__r"><u>nonce</u><code>${chal.nonce}</code></div>
          <div className="chal__r"><u>expires</u><code>${chal.expires_at_ts || "—"}</code></div>
        </div>`}
        <${Field} label="signature_b64" hint="Device-signed nonce · never generated in this browser">
          <${Input} icon="lock" value=${sig} onChange=${e=>setSig(e.target.value)} placeholder="base64 signature"/>
        <//>
        <button type="button" className="btn btn--go btn--sm" style=${{ marginTop:10 }}
          disabled=${busy || !chal || !custodyOn || !challengeOn}
          onClick=${verifySig}>Verify signature</button>
        ${error && html`<${Alert} tone="bad" title="Challenge error" say=${softActivateErrorOf(error)}/>`}
      <//>

      <${Card} title="Why this exists" meta="anti-substitution">
        <p className="sub">Identify proves a serial is <em>known</em>. It does not prove the board in
          front of you is the board that shipped as that serial.</p>
        <div className="rows">
          ${[["A cloned label","Anyone can print a label. Only the board holds the key."],
             ["A swapped board","The MAC is checked against the factory register when you claim."],
             ["A replayed signature","The one-time number is good for a single use inside 120 seconds."]].map(([t,s])=> html`
            <div key=${t} className="row row--say"><${Ico} d="shield" w=${15}/>
              <span className="txt"><b>${t}</b><span>${s}</span></span></div>`)}
        </div>
        <p className="note">MAC drift after claim does not silently pass either. The mismatch is
          fingerprinted and filed to the hardware drift report, which a platform admin reads. The
          MAC itself never appears in the log in readable form.</p>
      <//>
    </div>`;
}

/* ═══════════ 3 · claim ═════════════════════════════════════════════════ */
function Claim({ deviceId, setDeviceId, serviceId, setServiceId, zone, setZone,
                 identify, claimOut, setClaimOut, toast, seat, goStep }){
  const { run, busy, error, envelope } = useAction();
  const custodyOn = !!(seat && seat.custodyAllowed);
  const claimOn = !!(seat && seat.claimAllowed);
  const mac = String((identify && identify.mac_nocolon) || "").replace(/[^0-9A-Fa-f]/g, "").toUpperCase();
  const hwDefault = (identify && (identify.hw_id || identify.proposed_device_id)) || hwIdFromMac(mac);
  const claimable = !!(identify && CLAIMABLE.has(identify.status) && mac.length === 12);
  const claimed = !!(claimOut && (claimOut.device_id || claimOut.ok || claimOut.status === "claimed"));

  useEffect(() => {
    if (!deviceId && hwDefault) setDeviceId(hwDefault);
  }, [hwDefault]);

  const doClaim = async () => {
    if (!custodyOn || !claimOn) {
      toast("Claim is closed for this seat", "warn"); return;
    }
    if (!claimable) { toast("Identify a claimable board first", "warn"); return; }
    const did = String(deviceId || hwDefault || "").trim().toUpperCase();
    const sid = String(serviceId || "").trim().toUpperCase();
    const body = {
      mac_nocolon: mac,
      zone: String(zone || "all").trim() || "all",
    };
    // Omit device_id to let server default to CROC-<MAC>; send when operator overrides.
    if (did) body.device_id = did;
    if (sid) body.service_id = sid;
    const qr = identify && identify.qr_code;
    if (qr && String(qr).includes("|")) body.qr_code = qr;
    const out = await run(safeActivatePath("post__provision_claim"), { body });
    if (!out) { toast("Claim refused — see the panel", "bad"); return; }
    setClaimOut(out);
    toast("Custody taken · " + (out.device_id || did), "go");
  };

  return html`
    <div className="grid g-a">
      ${envelope
        ? html`<${KV} k="Last action" v=${envelopeSummary(envelope) || "—"} tone=${envelope.ok ? "hi" : "warn"}/>`
        : null}
      <${Card} title="Take custody" meta="take custody">
        ${!custodyOn ? html`<${Alert} tone="warn" title="Custody is closed for this seat"
          say=${softActivateErrorOf(seat && seat.detail) || "Claim needs activate operator."}/>` : null}
        ${!identify && html`<${Alert} tone="warn" title="No identify result yet"
          say="Pick a pending row or run Identify before claiming."/>`}
        ${identify && !claimable && html`<${Alert} tone="warn" title=${"Not claimable · " + (identify.status || "unknown")}
          say=${identify.message || "This identity cannot be claimed from the current state."}/>`}
        ${identify && identify.pop_pinned && html`<${Alert} tone="go" title="PoP pinned"
          say="Server already trusts this MAC’s software key — claim may proceed."/>`}
        <${Field} label="Hardware id (fleet / MQTT)" hint=${"Default CROC-" + (mac || "MAC") + ". Override only if you intentionally use SN- as fleet id."}>
          <${Input} icon="chip" value=${deviceId || hwDefault} onChange=${e=>setDeviceId(e.target.value)}/>
        <//>
        <${Field} label="Service S/N (optional)" hint="Sticker alias SN-… — kept as alias when Hardware id stays CROC-…">
          <${Input} icon="chip" value=${serviceId} onChange=${e=>setServiceId(e.target.value)}
            placeholder="SN-… optional"/>
        <//>
        <${Field} label="Zone" hint="1 — 31 characters. Defaults to “all”">
          <${Input} icon="map" value=${zone} onChange=${e=>setZone(e.target.value)}/>
        <//>
        <${Field} label="MAC" hint="Read-only — from identify / pending; checked against factory registry.">
          <${Input} icon="lock" value=${fmtMac(mac)} readOnly/>
        <//>
        <button type="button" className="btn btn--go btn--wide" style=${{ marginTop:6 }}
          disabled=${busy || !claimable || claimed || !custodyOn || !claimOn}
          onClick=${doClaim}>
          ${busy ? "Claiming…" : (claimed ? "Claimed · " + (claimOut.device_id || deviceId) : "Claim · " + (deviceId || hwDefault || "device"))}</button>
        ${error && html`<${Alert} tone="bad" title="Claim refused" say=${softActivateErrorOf(error)}/>`}
        <p className="note">Claiming raises the credential version (never reused). Broker ACL syncs;
          notification topics rebind; stale unbind jobs settle as reclaimed.</p>
      <//>

      <${Card} title=${claimed ? "Claim response" : "What you get back"} meta="issued on claim">
        ${!claimed && html`<p className="sub">Nothing has been written yet. Claim to see what comes back.</p>`}
        ${claimed && html`<${F}>
          <${KV} k="Device id" v=${claimOut.device_id || deviceId}/>
          <${KV} k="Hardware id" v=${claimOut.hw_id || hwDefault || "—"}/>
          <${KV} k="Service S/N" v=${claimOut.service_id || serviceId || "—"}/>
          <${KV} k="MAC" v=${fmtMac(claimOut.mac_nocolon || mac)}/>
          <${KV} k="Zone" v=${claimOut.zone || zone}/>
          <${KV} k="Credential version" v=${claimOut.cred_version != null ? String(claimOut.cred_version) : "—"}/>
          <${KV} k="Lifecycle" v=${claimOut.lifecycle_state || claimOut.lifecycle || claimOut.status || "—"} tone="good"/>
          <p className="note">Broker secrets are not echoed here in production. The device collects them
            over its own bootstrap channel, not through this screen.</p>
          <button type="button" className="btn btn--ghost btn--sm" onClick=${()=>goStep(5)}>
            Set position next</button>
        <//>`}
      <//>
    </div>

    <div className="grid g2 mt">
      <${Card} title="Every way this refuses" meta="claim errors">
        <table className="tbl">
          <thead><tr><th>What you will be told</th><th>What it means</th></tr></thead>
          <tbody>
            ${E.CLAIM_ERRORS.map(e=> html`
              <tr key=${e.d}>
                <td><b className=${e.tone}>${e.d}</b></td>
                <td style=${{ color:"var(--fg-3)" }}>${e.why}</td>
              </tr>`)}
          </tbody>
        </table>
      <//>
      <${Card} title="Two state axes, not one" meta="do not conflate">
        <p className="sub">A board carries a factory status and a lifecycle state at the same time. They
          move independently.</p>
        <u className="lbl">Factory status</u>
        <div className="rows" style=${{ marginBottom:14 }}>
          ${E.FACTORY_AXIS.map(s=> html`
            <div key=${s.id} className="row"><i className="stdot" data-tone=${s.tone}/>
              <span className="txt"><b>${s.id}</b><span>${s.say}</span></span></div>`)}
        </div>
        <u className="lbl">Lifecycle state</u>
        <div className="rows">
          ${E.LIFECYCLE_STATES.map(s=> html`
            <div key=${s.id} className="row"><i className="stdot" data-tone=${s.tone}/>
              <span className="txt"><b>${s.id}</b><span>${s.say}</span></span></div>`)}
        </div>
      <//>
    </div>`;
}

/* ═══════════ 4 · interfaces — Live W8 or blocked (no Soft arming) ══════ */
function Interfaces({ deviceId, ports, toast, seat }){
  const [sel,setSel] = useState("mipi_csi");
  const did = String(deviceId || "").trim();
  const wireOn = !!(seat && seat.wireAllowed === true);
  const boardPins = useApi(did && wireOn ? path("get__devices_p_pins", { pathParams: [did] }) : null);
  const loopLive = useApi(did && wireOn ? (safeBoardPath("get__devices_p_board-loop", { pathParams: [did] }) || null) : null);
  const loops = useMemo(() => boardLoopViewOf(loopLive.data), [loopLive.data]);
  const boardDisabled = !!(boardPins.error && /device_board|503|404|disabled/i.test(boardPins.error));
  const seats = (boardPins.data && (boardPins.data.items || boardPins.data.pins || boardPins.data.seats)) || [];
  const p = ports.find(x=>x.id===sel);

  return html`
    <${F}>
      ${!did
        ? html`<${Alert} tone="warn" title="Claim a device first"
            say="Interfaces write real board seats. Claim the node first, then bind here or on Machine → Hardware."/>`
        : !wireOn
          ? html`<${Alert} tone="warn" title="Wire seat closed"
              say="Post-claim board seats need a custody wire seat on this account."/>`
        : boardDisabled
          ? html`<${Alert} tone="warn" title="Board seats not available"
              say="Geometry below is orientation only. Open Machine → Hardware when board seats are available."/>`
          : html`<${Alert} tone="info" title="Board seats"
              say="Pins stay empty until pinmap or learn. Full binder: Machine → Hardware."/>`}

      <div className="grid g-b">
        <${M.section} className="card glass" initial=${{ opacity:0, y:14 }} animate=${{ opacity:1, y:0 }} transition=${SOFT}>
          <div className="card__in">
            <div className="sec__h">
              <h2 style=${{ margin:0, fontSize:"var(--t-read)", fontWeight:600 }}>The node</h2>
              <em style=${{ fontStyle:"normal", fontFamily:"var(--mono)", fontSize:"var(--t-label)", color:"var(--fg-3)" }}>
                geometry · orientation only</em>
            </div>
            <${Board} ports=${ports} sel=${sel} onPick=${setSel}/>
            <${BoardKey}/>
          </div>
        <//>
        <div className="col">
          <${Card} title="Connectors" meta=${ports.length + " physical"}>
            <${PortList} ports=${ports} sel=${sel} onPick=${setSel}/>
            <${AnimatePresence} mode="wait">
              ${p && html`<${PortDetail} key=${p.id} p=${p} onClose=${()=>setSel(null)}/>`}
            <//>
          <//>
          ${did && html`
            <${Card} title="Board seats" meta=${boardPins.loading ? "loading" : (seats.length + " seats")} cls="mt">
              ${!wireOn
                ? html`<p className="note">Wire seat closed — seats stay unread.</p>`
                : boardPins.error && !boardPins.data
                ? html`<p className="note">${softActivateErrorOf(boardPins.error)}</p>`
                : html`<div className="rows">
                    ${!seats.length ? html`<p className="note">No seats yet — wait for pinmap or open Machine → Hardware to learn.</p>` : null}
                    ${seats.slice(0, 16).map((s) => html`
                      <div key=${s.seat_id || s.id} className="row row--say">
                        <${Ico} d="chip" w=${14}/>
                        <span className="main">
                          <b>${s.seat_id || s.id}</b>
                          <span>${s.state || s.binding_state || "—"} · pin ${s.gpio == null ? "—" : s.gpio}
                            ${s.role ? " · " + s.role : ""}</span>
                        </span>
                      </div>`)}
                  </div>`}
              <div className="btn-row" style=${{ marginTop:12 }}>
                <button className="btn btn--go btn--sm"
                  onClick=${()=>go("/machine/"+encodeURIComponent(did))}>
                  Open machine hardware</button>
              </div>
            <//>`}
          ${did && html`
            <${Card} title="Closed loops" meta=${loopLive.loading ? "loading" : ((loops && loops.items.length) ? (loops.items.length + " live") : "awaiting")} cls="mt">
              ${!wireOn
                ? html`<p className="note">Wire seat closed — loops stay unread.</p>`
                : loopLive.error && !(loops && loops.items.length)
                  ? html`<p className="note">${softActivateErrorOf(loopLive.error)}</p>`
                  : html`<div className="machine-hw__loops" data-cmc-board-loop="1">
                      ${!(loops && loops.items.length)
                        ? html`<p className="note">No loop frame yet — wait for board-report or open Machine → Hardware.</p>`
                        : loops.items.map((x) => html`
                            <div key=${x.id} className="machine-hw__loop" data-tone=${x.tone}>
                              <b>${x.id}</b>
                              <span>${x.value}</span>
                            </div>`)}
                    </div>`}
              ${loops && loops.aiParked
                ? html`<p className="note">AI stays parked. mesh/not_live and locate/HOLD are honest, not missing UI.</p>`
                : null}
            <//>`}
        </div>
      </div>
      <${Card} title="Already wired on the board" meta="never invent GPIO" cls="mt">
        <p className="sub">Audio, network and card seats are spoken for before commissioning. Do not re-arm those reserved functions here.</p>
        ${E.PINS_SPOKEN_FOR.map(s=> html`
          <div key=${s.fn} className="spoken__r"><span>${s.fn}</span><code>${s.pins}</code></div>`)}
      <//>
    <//>`;
}

/* ═══════════ 5 · capabilities — Live registry write ═══════════════════ */
function Capabilities({ caps, setCaps, deviceId, toast, seat }){
  const [type,setType] = useState("edge_ai_node");
  const { run, busy, error, envelope } = useAction();
  const did = String(deviceId || "").trim();
  const wireOn = !!(seat && seat.wireAllowed === true);
  const boardPins = useApi(did && wireOn ? path("get__devices_p_pins", { pathParams: [did] }) : null);
  const seats = (boardPins.data && (boardPins.data.items || boardPins.data.pins || boardPins.data.seats)) || [];
  const catLive = useApi(wireOn ? path("get__devices_capabilities") : null);
  const oneLive = useApi(did && wireOn ? path("get__devices_p_capabilities", { pathParams: [did] }) : null);
  const catalogView = capabilityCatalogOf(catLive.data);
  // Soft≠Live: hydrate claimed from Live one-device row — Soft DEVICE_TYPES.caps seed deleted.
  useEffect(() => {
    const item = oneLive.data && oneLive.data.item;
    if (!item || !Array.isArray(item.claimed)) return;
    setCaps(item.claimed.map((c) => String(c).trim()).filter(Boolean));
    if (item.device_type) setType(String(item.device_type));
  }, [oneLive.data, did, setCaps]);
  // Soft≠Live: tested = Live seats only (verified|confirmed + role→cap). Never Soft E.PINS invent.
  const tested = useMemo(()=>{
    const fromSeats = (Array.isArray(seats) ? seats : [])
      .filter((x) => {
        const st = String(x.state || x.binding_state || "").toLowerCase();
        return (st === "verified" || st === "confirmed") && x.role;
      })
      .map((x) => E.PIN_ROLES.find((r) => r.id === x.role)?.cap)
      .filter(Boolean);
    return new Set(fromSeats);
  }, [seats]);
  const verifiedPreview = caps.filter(c=>tested.has(c));
  const rejected = caps.filter(c=>!tested.has(c));
  // Prefer server catalog ids when GET /devices/capabilities returns them; else closed E.CAP_CATALOG.
  const catalogRows = useMemo(() => {
    const liveIds = catalogView.catalog;
    if (liveIds.length) {
      const byId = new Map(E.CAP_CATALOG.map((c) => [c.id, c]));
      return liveIds.map((id) => byId.get(id) || { id, say: id, cls: "live", risk: "" });
    }
    return E.CAP_CATALOG;
  }, [catalogView.catalog]);

  const save = async () => {
    if (!wireOn) { toast("Wire seat is closed for this account", "warn"); return; }
    if (!did) { toast("Claim a device first — capabilities need a real device id", "warn"); return; }
    const out = await run(path("post__devices_p_capabilities", { pathParams: [did] }), {
      body: {
        device_type: type,
        claimed: caps,
        tested: [...tested],
        source: "cmc_activate",
      },
    });
    if (!out) {
      toast(/disabled|503/i.test(error || "")
        ? "Capability registry is off on this site"
        : softActivateErrorOf(error) || "Capability write refused", "bad");
      return;
    }
    const item = out.item || out;
    const liveVerified = Array.isArray(item.verified) ? item.verified : [];
    const persist = out.persistence || item.persistence || "—";
    toast("Capabilities registered · " + liveVerified.length + " verified · " + persist, "go");
  };

  return html`
    <${F}>
      ${envelope
        ? html`<${KV} k="Last action" v=${envelopeSummary(envelope) || "—"} tone=${envelope.ok ? "hi" : "warn"}/>`
        : null}
      <div className="grid g-a">
        <${Card} title="What this node claims" meta="register claims">
          <p className="sub">Pick a device type for the write. Catalog prefers the server capability list;
            closed brochure labels only fill names. Verified is claimed ∩ tested from board seats only.</p>
          ${!wireOn
            ? html`<${Alert} tone="warn" title="Wire seat closed"
                say="Capability catalog and writes need a post-claim wire seat."/>`
            : null}
          ${catLive.error && !catalogView.catalog.length
            ? html`<p className="note">Catalog unavailable · using closed labels · ${softActivateErrorOf(catLive.error)}</p>`
            : catalogView.catalog.length
              ? html`<p className="note">Catalog · ${catalogView.catalog.length} ids · ${catalogView.persistence || "—"}</p>`
              : null}
          ${!did
            ? html`<${Alert} tone="warn" title="No claimed device id"
                say="Finish Identify → Claim first."/>`
            : boardPins.loading
              ? html`<p className="note">Reading board seats for tested set…</p>`
              : boardPins.error && !seats.length
                ? html`<${Alert} tone="warn" title="Board seats unavailable"
                    say=${"tested stays empty until seats load · " + softActivateErrorOf(boardPins.error)}/>`
                : html`<p className="note">Board seats · ${seats.length} · tested caps ${tested.size}</p>`}
          <div className="chips" style=${{ marginBottom:16 }}>
            ${E.DEVICE_TYPES.map(t=> html`
              <button key=${t.id} className="chip" data-on=${type===t.id?"1":"0"}
                onClick=${()=>{ setType(t.id); /* Soft brochure caps seed deleted — type id only */ }}>
                ${t.label}${t.design && html`<span className="tiny"> · design</span>`}</button>`)}
          </div>
          <div className="capgrid">
            ${catalogRows.map(c=>{
              const on = caps.includes(c.id), ok = tested.has(c.id);
              return html`
                <button key=${c.id} className="capsel" data-on=${on?"1":"0"} data-ok=${on&&ok?"1":"0"}
                    data-risk=${c.risk||""}
                    onClick=${()=>setCaps(v=> on ? v.filter(x=>x!==c.id) : [...v,c.id])}>
                  <span className="capsel__h">
                    <code>${c.id}</code>
                    <i className="capsel__cls" data-cls=${c.cls}>${c.cls}</i>
                  </span>
                  <em>${c.say}</em>
                  ${on && html`<span className="capsel__v" data-ok=${ok?"1":"0"}>
                    ${ok ? "in tested set" : "claimed only"}</span>`}
                </button>`;
            })}
          </div>
          <button type="button" className="btn btn--go desk-craft__act" style=${{ marginTop:14 }} disabled=${busy || !did || !wireOn} onClick=${save}>
            ${busy ? "Writing…" : "Save capabilities"}</button>
          <div className="btn-row" style=${{ marginTop: 10, flexWrap: "wrap", gap: 8 }}>
            <button type="button" className="btn btn--ghost btn--sm desk-craft__act"
              onClick=${() => go("/skills/assign" + (did ? "?device=" + encodeURIComponent(did) : ""))}>
              Skills warehouse · function packs
            </button>
            <button type="button" className="btn btn--ghost btn--sm desk-craft__act"
              disabled=${!did}
              onClick=${() => did && go("/machine/" + encodeURIComponent(did))}>
              Open Machine Skills
            </button>
          </div>
          ${error && html`<${Alert} tone="bad" title="Write failed" say=${softActivateErrorOf(error)}/>`}
        <//>

        <div className="col">
          <${Card} title="What the gate will expose" meta="Verified = claimed that passed tests">
            <${KV} k="device" v=${did || "—"}/>
            <${KV} k="claimed" v=${caps.length}/>
            <${KV} k="verified (preview)" v=${verifiedPreview.length} tone="good"/>
            <${KV} k="rejected" v=${rejected.length} tone=${rejected.length?"warn":""}/>
            ${rejected.length>0 && html`
              <div className="rejbox">
                <u>Dropped from verified on write</u>
                <div className="chips">${rejected.map(r=> html`<span key=${r} className="chip" data-tone="bad">${r}</span>`)}</div>
              </div>`}
          <//>
          <${Card} title="Capability registry" meta="site product">
            <p className="sub">When the capability registry is off on this site the write fails honestly.
              We never toast a pretend success. With the registry on, persistence is the platform database.</p>
          <//>
        </div>
      </div>
    <//>`;
}

/* ═══════════ 6 · position ══════════════════════════════════════════════ */
function Position({ deviceId, zone: claimZone, toast, seat }){
  const [lat,setLat] = useState("");
  const [lng,setLng] = useState("");
  const [zone,setZone] = useState(claimZone || "all");
  const { run, busy, error, envelope } = useAction();
  const did = String(deviceId || "").trim();
  const wireOn = !!(seat && seat.wireAllowed === true);

  const save = async () => {
    if (!wireOn) { toast("Wire seat is closed for this account", "warn"); return; }
    if (!did) { toast("Claim a device first — position needs a real device id", "warn"); return; }
    const la = Number(lat), ln = Number(lng);
    if (!Number.isFinite(la) || !Number.isFinite(ln)) {
      toast("Latitude and longitude must both be numbers", "bad"); return;
    }
    const out = await run(path("patch__devices_p_profile", { pathParams: [did] }), {
      method: "PATCH",
      body: { map_lat: la, map_lng: ln, zone: String(zone || "all").trim() || "all" },
    });
    if (!out) { toast(softActivateErrorOf(error) || "Survey write refused", "bad"); return; }
    toast("Survey recorded · " + did, "go");
  };

  return html`
    <${F}>
      ${envelope
        ? html`<${KV} k="Last action" v=${envelopeSummary(envelope) || "—"} tone=${envelope.ok ? "hi" : "warn"}/>`
        : null}
      <${M.div} className="law glass" initial=${{ opacity:0, y:12 }} animate=${{ opacity:1, y:0 }} transition=${SOFT}>
        <div className="law__in">
          <span className="law__k">position law</span>
          <h2>${E.POS_LAW.title}</h2>
          <p>${E.POS_LAW.say}</p>
          <div className="law__map">
            <div className="law__pt" data-real="1"><i/><b>Sarawak</b><em>where the responder is</em></div>
            <div className="law__x"><${Ico} d="ban" w=${16}/></div>
            <div className="law__pt"><i/><b>Kuala Lumpur</b><em>where the traffic leaves</em></div>
          </div>
          <span className="law__cite">${E.POS_LAW.cite}</span>
        </div>
      <//>

      <div className="grid g-a mt">
        <${Card} title="Survey this node" meta="survey position">
          ${!wireOn && html`<${Alert} tone="warn" title="Wire seat closed"
            say="Survey writes need a post-claim wire seat."/>`}
          ${!did && html`<${Alert} tone="warn" title="No claimed device id"
            say="Finish Identify → Claim first. This page will not invent coordinates."/>`}
          <p className="sub">The board carries no GNSS receiver and no software may invent one. A fixed
            node's position is recorded by the person mounting it, and that recording is the truth.</p>
          <${KV} k="Device" v=${did || "—"}/>
          <div className="grid g2">
            <${Field} label="Latitude"><${Input} icon="target" value=${lat} onChange=${e=>setLat(e.target.value)}/><//>
            <${Field} label="Longitude"><${Input} icon="target" value=${lng} onChange=${e=>setLng(e.target.value)}/><//>
          </div>
          <${Field} label="Zone" hint="Site → building → floor → zone · written with the survey.">
            <${Input} icon="map" value=${zone} onChange=${e=>setZone(e.target.value)}/>
          <//>
          <button type="button" className="btn btn--go btn--wide" disabled=${busy || !did || !wireOn} onClick=${save}>
            ${busy ? "Writing…" : "Record survey position"}</button>
          ${error && html`<${Alert} tone="bad" title="Survey refused" say=${softActivateErrorOf(error)}/>`}
          <p className="note">Kept as this node's fixed position and marked as set by hand, so nobody
            later mistakes it for something the board worked out itself.</p>
        <//>

        <${Card} title="Where a position may come from" meta="in priority order">
          ${E.POS_SOURCES.map(s=> html`
            <div key=${s.id} className="src" data-state=${s.state}>
              <b className="src__r">${s.rank}</b>
              <span className="src__t">
                <b>${s.label}<i className="src__st" data-state=${s.state}>${s.state}</i></b>
                <em>${s.say}</em>
                <code>${s.answer}</code>
              </span>
              <span className="src__e">${s.err}</span>
            </div>`)}
        <//>
      </div>

      <div className="grid g2 mt">
        <${Card} title="Refused as a position source" meta="never written">
          <div className="chips" style=${{ marginBottom:12 }}>
            ${E.POS_FORBIDDEN.map(f=> html`<span key=${f.id} className="chip" data-tone="bad">
              <${Ico} d="ban" w=${11}/> ${f.label}</span>`)}
          </div>
          <p className="note">These are not merely deprioritised. A value from any of them is refused
            outright, so it never becomes the node's recorded position and it never reaches a dispatch
            ranking.</p>
        <//>
        <${Card} title="What gets dropped on the way in" meta="positions the phone sends up">
          <div className="grid g2 tight">
            ${E.GPS_QUALITY.map(q=> html`
              <div key=${q.k} className="mini"><u>${q.k}</u><code>${q.v}</code></div>`)}
          </div>
        <//>
      </div>
    <//>`;
}

/* ═══════════ 7 · done ══════════════════════════════════════════════════ */
function Done({ deviceId, zone, caps, claimOut, toast, seat, goStep }){
  const did = String((claimOut && claimOut.device_id) || deviceId || "").trim();
  const wireOn = !!(seat && seat.wireAllowed === true);
  const boardPins = useApi(did && wireOn ? path("get__devices_p_pins", { pathParams: [did] }) : null);
  const seats = (boardPins.data && (boardPins.data.items || boardPins.data.pins || boardPins.data.seats)) || [];
  const bound = seats.filter((s) => s.role || s.binding_state === "claimed" || s.state === "claimed" || s.state === "verified" || s.state === "confirmed");
  const boardOff = !!(boardPins.error && /device_board|503|404|disabled/i.test(boardPins.error));
  const cred = claimOut && claimOut.cred_version != null ? String(claimOut.cred_version) : "—";
  const label = did || "—";
  const { run, busy, error, envelope } = useAction();
  const [ssid, setSsid] = useState("");
  const [wifiPass, setWifiPass] = useState("");
  const [taskId, setTaskId] = useState("");
  const taskPath = did && taskId && wireOn
    ? path("get__devices_p_provision_wifi-task_p", { pathParams: [did, taskId] })
    : null;
  const task = useApi(taskPath);
  useEffect(() => {
    if (!taskPath) return undefined;
    const id = setInterval(() => { if (task.reload) task.reload(); }, 2500);
    return () => clearInterval(id);
  }, [taskPath, task.reload]);

  const startWifi = async () => {
    if (!wireOn) { toast && toast("Wire seat is closed for this account", "warn"); return; }
    if (!did) { toast && toast("Claim a device first", "warn"); return; }
    if (!ssid.trim()) { toast && toast("SSID required", "bad"); return; }
    const out = await run(path("post__devices_p_provision_wifi-task", { pathParams: [did] }), {
      method: "POST",
      body: { ssid: ssid.trim(), password: wifiPass || "", chain: [] },
    });
    if (!out) { toast && toast(softActivateErrorOf(error) || "Wi-Fi task refused", "bad"); return; }
    const tid = out.task_id || out.id || "";
    setTaskId(String(tid));
    toast && toast("Wi-Fi task started · " + (tid || "ok"), "go");
  };

  const taskRow = (task.data && (task.data.item || task.data)) || null;
  const taskStatus = taskRow && (taskRow.status || taskRow.state);

  return html`
    <${F}>
      ${envelope
        ? html`<${KV} k="Last action" v=${envelopeSummary(envelope) || "—"} tone=${envelope.ok ? "hi" : "warn"}/>`
        : null}
      <${M.div} className="doneart" initial=${{ opacity:0, scale:.96 }} animate=${{ opacity:1, scale:1 }} transition=${SOFT} data-cmc-act="act-1">
        <div className="doneart__ring"><i/><i/><i/></div>
        <h2>${label} ${claimOut ? "is in custody" : "— claim not confirmed in this session"}</h2>
        <p>${claimOut
          ? html`Claim response received · credential version <b>${cred}</b>. Summary below is board seats + claimed capabilities.`
          : html`Finish Identify → Claim before treating this as custody.`}</p>
        <div className="btn-row" style=${{ marginTop: 12, justifyContent: "center", flexWrap: "wrap", gap: 8 }}>
          <button type="button" className="btn btn--go btn--sm"
            disabled=${!did}
            onClick=${()=> did && go("/machine/"+encodeURIComponent(did))}>
            Open this machine</button>
          <button type="button" className="btn btn--ghost btn--sm" onClick=${()=>go("/machine")}>
            Machine list</button>
          <button type="button" className="btn btn--ghost btn--sm" onClick=${()=>goStep(0)}>
            Activate again</button>
        </div>
      <//>

      <div className="grid g3 mt">
        <${Card} title="Custody" meta=${claimOut ? "claim recorded" : "incomplete"}>
          <${KV} k="Device id" v=${label}/>
          <${KV} k="Zone" v=${(claimOut && claimOut.zone) || zone || "—"}/>
          <${KV} k="Lifecycle" v=${(claimOut && (claimOut.lifecycle_state || claimOut.lifecycle || claimOut.status)) || "—"} tone=${claimOut?"good":""}/>
          <${KV} k="Credential version" v=${cred}/>
        <//>
        <${Card} title="Interfaces" meta=${!wireOn ? "wire closed" : (boardOff ? "board flag off" : (boardPins.loading ? "loading" : "board seats"))}>
          ${!wireOn
            ? html`<p className="note">Wire seat closed — seats unread.</p>`
            : boardPins.error && !boardPins.data
            ? html`<p className="note">${softActivateErrorOf(boardPins.error)}</p>`
            : html`<${F}>
                <${KV} k="Seats returned" v=${String(seats.length)}/>
                <${KV} k="Bound / confirmed" v=${String(bound.length)} tone=${bound.length?"good":"warn"}/>
                <${KV} k="Binder" v="Machine → Hardware" tone="warn"/>
              <//>`}
        <//>
        <${Card} title="Capabilities" meta="wizard claimed set">
          <${KV} k="Claimed in wizard" v=${caps.length ? caps.join(" · ") : "none"}/>
          <${KV} k="Count" v=${String(caps.length)}/>
          <${KV} k="Registry write" v=${wireOn ? "armed when registry on" : "wire closed"}/>
        <//>
      </div>

      <${Card} title="Provision Wi-Fi" meta="Wi-Fi provision" cls="mt">
        <p className="sub">Queues Wi-Fi config on the claimed device and polls task status. We never invent “already online”.</p>
        ${!wireOn
          ? html`<${Alert} tone="warn" title="Wire seat closed" say="Wi-Fi provision needs a post-claim wire seat."/>`
          : !did
          ? html`<${Alert} tone="warn" title="No device id" say="Claim first — Wi-Fi task needs a claimed device."/>`
          : html`<${F}>
              <div className="grid g2">
                <${Field} label="SSID"><${Input} icon="radio" value=${ssid}
                  onChange=${(e)=>setSsid(e.target.value)} placeholder="site-ops"/><//>
                <${Field} label="Password"><${Input} icon="lock" type="password" value=${wifiPass}
                  onChange=${(e)=>setWifiPass(e.target.value)} autocomplete="new-password"/><//>
              </div>
              <div className="btn-row" style=${{ marginTop:12 }}>
                <button type="button" className="btn btn--go btn--sm" disabled=${busy || !ssid.trim() || !wireOn}
                  onClick=${startWifi}>${busy ? "Queuing…" : "Start Wi-Fi task"}</button>
              </div>
              ${taskId && html`<${F}>
                <${KV} k="Task" v=${taskId}/>
                <${KV} k="Status" v=${taskStatus || (task.loading ? "…" : (task.error ? softActivateErrorOf(task.error) : "pending"))}
                  tone=${taskStatus==="success"?"good":(taskStatus==="failed"?"bad":"warn")}/>
                ${taskRow && taskRow.message ? html`<p className="note">${taskRow.message}</p>` : null}
              <//>`}
              ${error && html`<${Alert} tone="warn" title="Wi-Fi task error" say=${softActivateErrorOf(error)}/>`}
            <//>`}
      <//>

      <${Card} title="What happens next, without you" meta="after claim" cls="mt">
        <div className="rows">
          ${[["The board is told","The claim is pushed out to the device, which picks up its credentials and resyncs."],
             ["Publish rights synchronised","The broker accepts exactly this identity on exactly this device's channels."],
             ["Notifications rebound","Alert topics are moved across to the new owner."],
             ["Stale jobs settled","Any unbind job still in flight is closed off as reclaimed."],
             ["Audit","A claim entry is written naming you, the device, its MAC, the zone and the owner."]].map(([t,s])=> html`
            <div key=${t} className="row row--say"><${Ico} d="check" w=${14}/>
              <span className="txt"><b>${t}</b><span>${s}</span></span></div>`)}
        </div>
      <//>
    <//>`;
}
